import { useEffect, useState } from 'react';

// 로그인 상태처럼 "브라우저를 닫으면 사라져야 하지만, 열려 있는 다른 탭과는 공유되어야 하는" 값을 위한 useState.
// sessionStorage는 탭마다 따로라서 새 탭에서는 비어 있으므로, 새 탭이 열리면 localStorage 이벤트로 다른 탭에 값을 요청해 받아온다.
// localStorage에는 전달용 신호만 잠깐 썼다 지우므로 모든 창을 닫으면 값이 남지 않는다(= 자동 로그아웃).

const REQUEST_KEY = '__tabSession:request';
const RESPONSE_PREFIX = '__tabSession:response:';
const UPDATE_PREFIX = '__tabSession:update:';

const listeners = new Map<string, Set<(value: unknown) => void>>();

function notify<T>(key: string, value: T) {
  listeners.get(key)?.forEach((listener) => listener(value));
}

function readSession<T>(key: string): T | undefined {
  try {
    const stored = sessionStorage.getItem(key);
    return stored !== null ? (JSON.parse(stored) as T) : undefined;
  } catch {
    return undefined;
  }
}

function writeSession<T>(key: string, value: T) {
  try {
    if (value === null || value === undefined) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // sessionStorage를 사용할 수 없는 환경이면 메모리 상태만 갱신한다
  }
}

// 다른 탭에 신호를 보낸다. storage 이벤트는 값이 바뀔 때만 발생하므로 쓰고 바로 지운다
function broadcast(signalKey: string, payload: unknown) {
  try {
    localStorage.setItem(signalKey, JSON.stringify({ payload, at: Date.now() }));
    localStorage.removeItem(signalKey);
  } catch {
    // localStorage를 사용할 수 없으면 탭 간 공유 없이 동작한다
  }
}

const syncedKeys = new Set<string>();

// 탭(문서)마다 한 번만 storage 이벤트를 구독한다
function ensureSync(key: string) {
  if (syncedKeys.has(key) || typeof window === 'undefined') return;
  syncedKeys.add(key);

  window.addEventListener('storage', (event) => {
    if (!event.newValue) return;
    let payload: unknown;
    try {
      payload = (JSON.parse(event.newValue) as { payload: unknown }).payload;
    } catch {
      return;
    }

    // 새 탭이 값을 요청하면, 값을 가진 탭이 응답한다
    if (event.key === REQUEST_KEY && payload === key) {
      const current = readSession(key);
      if (current !== undefined) broadcast(`${RESPONSE_PREFIX}${key}`, current);
      return;
    }
    // 요청에 대한 응답: 아직 값이 없는 탭만 받아들인다
    if (event.key === `${RESPONSE_PREFIX}${key}`) {
      if (readSession(key) === undefined && payload != null) {
        writeSession(key, payload);
        notify(key, payload);
      }
      return;
    }
    // 다른 탭에서 로그인/로그아웃 등으로 값이 바뀌면 함께 반영한다
    if (event.key === `${UPDATE_PREFIX}${key}`) {
      writeSession(key, payload);
      notify(key, payload ?? null);
    }
  });

  if (readSession(key) === undefined) {
    broadcast(REQUEST_KEY, key);
  }
}

export function useTabSessionState<T>(key: string, defaultValue: T) {
  const [value, setValue] = useState<T>(() => {
    ensureSync(key);
    const stored = readSession<T>(key);
    return stored !== undefined ? stored : defaultValue;
  });

  useEffect(() => {
    const listener = (next: unknown) => setValue((next ?? defaultValue) as T);
    if (!listeners.has(key)) listeners.set(key, new Set());
    listeners.get(key)!.add(listener);
    // 구독 전에 다른 탭의 응답이 먼저 도착해 저장만 된 경우를 반영한다
    const stored = readSession<T>(key);
    if (stored !== undefined) setValue(stored);
    return () => {
      listeners.get(key)!.delete(listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const update = (next: T | ((prev: T) => T)) => {
    setValue((prev) => {
      const resolved = typeof next === 'function' ? (next as (prev: T) => T)(prev) : next;
      writeSession(key, resolved);
      notify(key, resolved);
      broadcast(`${UPDATE_PREFIX}${key}`, resolved ?? null);
      return resolved;
    });
  };

  return [value, update] as const;
}
